import { useEffect, useRef } from "react";
import { makeMathRenderer, DISPLAY } from "@pearsontechnology/bronte-lib-math/readonly";
import { NODE_TYPE_MATHML_EXPRESSION } from "./constants";
import { ExpressionWrapper } from "./styles";
import {
  buildReadonlyMathRendererOptions,
  createReadonlyMathStateAdapter,
} from "./readonlyMathMount";
import { withInlineVoidElement } from "../../utils";

const INVALID_MATH_EXPRESSION = "invalid expression";

function isRenderableSvgFallback(svg: string | undefined): svg is string {
  return !!svg && svg !== INVALID_MATH_EXPRESSION && svg.trim().startsWith("<svg");
}

type MathMlReadOnlyOfflineProps = {
  attributes: Record<string, unknown>;
  mathML: string | undefined;
  svg?: string;
  alt?: string;
  display: string | undefined;
};

function MathMlReadOnlyOffline({
  attributes,
  mathML,
  svg,
  alt,
  display,
}: MathMlReadOnlyOfflineProps) {
  const mathNodeRef = useRef<HTMLDivElement | null>(null);

  const rawMathML = mathML ?? "";
  const svgOnly = !String(rawMathML).trim() && isRenderableSvgFallback(svg);
  const displayMode = display ?? DISPLAY.INLINE;
  const isBlock = displayMode === DISPLAY.BLOCK || displayMode === "block";

  useEffect(() => {
    if (svgOnly) {
      return;
    }
    const node = mathNodeRef.current;
    if (!node) {
      return;
    }
    const stateAdapter = createReadonlyMathStateAdapter(rawMathML);
    const renderer = makeMathRenderer(stateAdapter)(
      node,
      buildReadonlyMathRendererOptions(isBlock),
    );
    return () => {
      renderer.destroy();
    };
  }, [rawMathML, isBlock, svgOnly]);

  if (svgOnly) {
    return (
      <ExpressionWrapper
        {...attributes}
        data-display={isBlock ? "block" : "inline"}
        className="spf-inline-expression"
        tabIndex={0}
        aria-label={alt}
        role="img"
        aria-roledescription="math"
      >
        <span dangerouslySetInnerHTML={{ __html: svg ?? "" }} />
      </ExpressionWrapper>
    );
  }

  return (
    <ExpressionWrapper
      {...attributes}
      data-display={isBlock ? "block" : "inline"}
      className="spf-inline-expression"
      tabIndex={0}
      aria-label={alt}
      role="img"
      aria-roledescription="math"
    >
      <div ref={mathNodeRef} />
    </ExpressionWrapper>
  );
}

export default function MathMLExpressionOffline() {
  const plugin = (editor: any) => {
    withInlineVoidElement(editor, NODE_TYPE_MATHML_EXPRESSION, true);
    return editor;
  };

  plugin.renderElement = ({ element, attributes }: any) => {
    if (element.type !== NODE_TYPE_MATHML_EXPRESSION) {
      return undefined;
    }
    const data = element.data || {};
    const mathML = data.mathML;
    const svg = data.svg;
    const alt = data.alt;
    const display = data.display;

    return (
      <MathMlReadOnlyOffline
        attributes={attributes}
        mathML={mathML}
        svg={svg}
        alt={alt}
        display={display}
      />
    );
  };

  return plugin;
}
